import React, { memo, useEffect } from "react";
import Select, { StylesConfig } from "react-select";
import { useUpdateUserDataMutation } from "../../api/userAPI";
import {
  CultureCodeE,
  DirectionE,
  IconColorE,
  IconSizeE,
  LangE,
} from "../../models/enums";
import { SelectOptionT } from "../../models/models";
import { useAppSelector } from "../../store/hooks";
import { selectDirection } from "../../store/selectors/appSelectors";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { switchLang } from "../../store/slices/appSlice";
import {
  changeCurrentUserLangLocalStorage,
  getGuestLangFromStorage,
} from "../../utils/helpers/tokenStorage";
import Icon from "../Icon/Icon";
import useReactConsts from "../../utils/helpers/auth/useReactConsts";
import useQueryParams from "../../utils/hooks/useQueryParams";
import { reloadChatWidget, updateHtmlLangAttribute } from "../../utils/helpers/chatWidget";
import { useAuth } from "../../utils/hooks/useAuth";
import { useCurrentUserProfile } from "../../utils/hooks/useCurrentUserProfile";

import styles from "./styles.module.scss";
import selectStyles from "./selectStyles";

const langOptions: SelectOptionT[] = [
  { value: CultureCodeE.EN, label: "EN" },
  { value: CultureCodeE.HE, label: "HE" },
];

interface LangSelectI {
  selectStyles?: StylesConfig<SelectOptionT, false>;
}

const LangSelect = memo((props: LangSelectI) => {
  const { selectStyles: outerSelectStyles = {} } = props;
  const user = useAppSelector(selectCurrentUser);
  // spec-42 — identity-scoped read. `profile` is the CURRENT user's row or nothing; it is never the
  // previously logged-in user's row (see useCurrentUserProfile). Do not go back to
  // `useGetUserByIdQuery(...).data` here: with that stale fallback, handleChange spread ANOTHER
  // account's FirstName/LastName/Email/PhoneNumber into a PUT under the current user's id and the
  // server answered 200 — a language change that silently overwrote the profile.
  const { profile, isFetching } = useCurrentUserProfile();
  const [updateUserData, { isLoading }] = useUpdateUserDataMutation();
  const direction = useAppSelector(selectDirection);
  const setReactConsts = useReactConsts();
  const { currentUser } = useAuth();
  const { addQueryParam } = useQueryParams();

  useEffect(() => {
    if (profile) {
      const lang =
        currentUser?.culture === CultureCodeE.EN ? LangE.EN : LangE.HE;
      switchLang(lang);
    }
  }, [currentUser?.culture, user?.userId]);

  const handleChange = async (e) => {
    // D4 — guard the SAVE, not the language switch. A missing profile (mid account switch) must not
    // stop the user changing the UI language; it must only stop us PUTting a body we cannot vouch for.
    if (profile && user?.userId) {
      const body = {
        ...profile,
        Culture: e.value,
        Id: user.userId,
        EmailConfirmed: user.EmailConfirmed,
        // Preserve the contact-info gate flag so a language save never resets it (which would
        // re-trigger the mandatory popup). Mirrors how EmailConfirmed is round-tripped.
        ContactInfoConfirmed: user.ContactInfoConfirmed,
      };

      // update lang for user on server side
      await updateUserData(body);
    }

    // change global site translations
    const newLang = e.value === CultureCodeE.EN ? LangE.EN : LangE.HE;
    await setReactConsts(newLang);


    // update HTML lang attribute before reloading chat widget
    updateHtmlLangAttribute(newLang);

    // reload chat widget with new language
    reloadChatWidget(newLang);

    // update locale storage
    changeCurrentUserLangLocalStorage(e.value);

    // update URL parameter with language
    const langParam = e.value === CultureCodeE.EN ? "en" : "he";
    addQueryParam({ lang: langParam });
  };

  return (
    <div className={styles.selectWrapper}>
      {!isFetching ? (
        /*@ts-ignore*/
        <Select
          options={langOptions}
          menuPlacement="top"
          styles={{...selectStyles, ...outerSelectStyles}}
          blurInputOnSelect
          defaultValue={langOptions?.find(
            ({ value }) =>
              value === (profile?.Culture ?? getGuestLangFromStorage() ?? CultureCodeE.EN)
          )}
          onChange={handleChange}
          isRtl={direction === DirectionE.RTL}
          isSearchable={false}
          isClearable={false}
          menuPortalTarget={document.body}
          className={styles.langSelect}
        />
      ) : (
        <div />
      )}
      {isLoading && (
        <Icon
          name="loading"
          size={IconSizeE.S}
          color={IconColorE.GRAY}
          className={styles.loadingIcon}
        />
      )}
    </div>
  );
});

export default LangSelect;
